import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { PeriodisationRowFormValue, PeriodisationRowsFormValue } from "models/formTypes/incomingInvoice";
import { DateRange } from "models/formTypes/util";
import { PeriodisationMode } from "models/utilTypes/periodisationTypes";
import { startOfMonth } from "date-fns";
import DateHelper from "./dateHelper";
import MathHelper from "./mathHelper";

type ApiRow = WintSuperkollAPIModel.IncomingInvoicePeriodisationRowModel;

// The generated client types dates as Date but the API delivers ISO strings.
const toDate = (value: unknown): Date | undefined => (value ? DateHelper.parseDateOnly(value as string) : undefined);

// Core stores the picker's end (23:59:59.999) unchanged. Read back as midnight, a one-day range would get
// start == end, which Core rejects (RowPeriodOrder), and an unchanged save would no longer equal what Core holds.
const dateRangeFromApi = (start: unknown, end: unknown): DateRange => {
  const endDate = toDate(end);
  return { start: toDate(start), end: endDate && DateHelper.getEndOfDay(endDate) };
};

const rangeFromApi = (row: ApiRow, mode: PeriodisationMode): DateRange | undefined => {
  if (mode === "date") return dateRangeFromApi(row.periodisationStartDate, row.periodisationEndDate);
  if (!row.periodisationStartYear || !row.periodisationStartMonth) return undefined;
  const start = DateHelper.getDateFromYearMonth(row.periodisationStartYear * 100 + row.periodisationStartMonth);
  const end =
    row.periodisationEndYear && row.periodisationEndMonth
      ? DateHelper.getLastDayOfMonth(
          DateHelper.getDateFromYearMonth(row.periodisationEndYear * 100 + row.periodisationEndMonth)
        )
      : undefined;
  return { start, end };
};

const isRowComplete = (row: PeriodisationRowFormValue): boolean => !!row.range?.start && !!row.range?.end;

// Without a known invoice month nothing counts as lying in it.
const isEntirelyInMonth = (row: PeriodisationRowFormValue, invoiceMonth: Date | undefined): boolean =>
  !!invoiceMonth &&
  DateHelper.getYearMonth(row.range!.start!) === DateHelper.getYearMonth(invoiceMonth) &&
  DateHelper.getYearMonth(row.range!.end!) === DateHelper.getYearMonth(invoiceMonth);

const sendableRows = (value: PeriodisationRowsFormValue): PeriodisationRowFormValue[] =>
  value.rows.filter((r) => isRowComplete(r) && (r.amount ?? 0) > 0);

// Core rejects a split where every row lies in the invoice month (RowsAllInInvoiceMonth): that is no periodisation.
const isSplitAllInInvoiceMonth = (value: PeriodisationRowsFormValue, invoiceMonth: Date | undefined): boolean => {
  const rows = sendableRows(value);
  return value.isSplit && rows.length > 0 && rows.every((r) => isEntirelyInMonth(r, invoiceMonth));
};

// Core accepts a one-row split, but a single range is the canonical shape: fromInvoice reads it back unsplit, so every
// save path must send it the same way or an unchanged save would differ from what Core holds.
const rowsToSend = (
  value: PeriodisationRowsFormValue | undefined,
  invoiceMonth: Date | undefined
): PeriodisationRowFormValue[] => {
  if (!value) return [];
  if (!value.isSplit) return value.rows[0] && isRowComplete(value.rows[0]) ? [value.rows[0]] : [];
  if (isSplitAllInInvoiceMonth(value, invoiceMonth)) return [];
  return sendableRows(value);
};

const envelope = (
  rows: PeriodisationRowFormValue[],
  mode: PeriodisationMode
): {
  periodisationStart?: Date;
  periodisationEnd?: Date;
  periodisationStartDate?: Date;
  periodisationEndDate?: Date;
} => {
  if (rows.length === 0) return {};
  const start = new Date(Math.min(...rows.map((r) => r.range!.start!.getTime())));
  const end = new Date(Math.max(...rows.map((r) => r.range!.end!.getTime())));
  // startOfMonth also drops the time: an end-of-day 23:59:59.999 would round up to the 2nd in SQL datetime.
  const months = {
    periodisationStart: startOfMonth(start),
    periodisationEnd: startOfMonth(end),
  };
  return mode === "date" ? { ...months, periodisationStartDate: start, periodisationEndDate: end } : months;
};

const toApiRow = (row: PeriodisationRowFormValue, mode: PeriodisationMode): ApiRow => {
  const start = row.range!.start!;
  const end = row.range!.end!;
  if (mode === "date") {
    return { amount: row.amount ?? 0, periodisationStartDate: start, periodisationEndDate: end };
  }
  return {
    amount: row.amount ?? 0,
    periodisationStartYear: DateHelper.getYear(start),
    periodisationStartMonth: DateHelper.getMonth(start),
    periodisationEndYear: DateHelper.getYear(end),
    periodisationEndMonth: DateHelper.getMonth(end),
  };
};

const PeriodisationRowsHelper = {
  /** True when the API knows about rows (new Core). Old Core leaves the property undefined. */
  isSplitSupported: (invoice: WintSuperkollAPIModel.IncomingInvoice): boolean =>
    invoice.periodisationRows !== undefined,

  fromInvoice: (invoice: WintSuperkollAPIModel.IncomingInvoice): PeriodisationRowsFormValue => {
    const apiRows = invoice.periodisationRows ?? [];
    if (apiRows.length > 1) {
      const mode: PeriodisationMode = apiRows.some((r) => r.periodisationStartDate) ? "date" : "month";
      return { mode, isSplit: true, rows: apiRows.map((r) => ({ amount: r.amount, range: rangeFromApi(r, mode) })) };
    }
    const mode: PeriodisationMode = invoice.periodisationStartDate ? "date" : "month";
    const { start, end } =
      mode === "date"
        ? dateRangeFromApi(invoice.periodisationStartDate, invoice.periodisationEndDate)
        : { start: toDate(invoice.periodisationStart), end: toDate(invoice.periodisationEnd) };
    return { mode, isSplit: false, rows: [{ range: start || end ? { start, end } : undefined }] };
  },

  /** The advanced dialog always edits rows: an unsplit value opens as one row holding the whole net. */
  toDialogValue: (value: PeriodisationRowsFormValue | undefined, netAmount: number): PeriodisationRowsFormValue =>
    value?.isSplit
      ? { ...value, rows: value.rows.map((r) => ({ ...r })) }
      : { mode: value?.mode ?? "month", isSplit: true, rows: [{ amount: netAmount, range: value?.rows[0]?.range }] },

  /** Several rows are a split; a single row is the canonical unsplit shape that fromInvoice also reads back. */
  fromDialogValue: (value: PeriodisationRowsFormValue): PeriodisationRowsFormValue =>
    value.rows.length > 1
      ? { ...value, isSplit: true }
      : { mode: value.mode, isSplit: false, rows: [{ range: value.rows[0]?.range }] },

  hasPeriodisation: (value?: PeriodisationRowsFormValue): boolean =>
    !!value && value.rows.some((r) => !!r.range?.start || !!r.range?.end),

  allocated: (value: PeriodisationRowsFormValue | undefined, netAmount: number): number =>
    !value || !value.isSplit ? netAmount : value.rows.reduce((sum, r) => MathHelper.addAmounts(sum, r.amount ?? 0), 0),

  remaining: (value: PeriodisationRowsFormValue | undefined, netAmount: number): number =>
    MathHelper.subtractAmounts(netAmount, PeriodisationRowsHelper.allocated(value, netAmount)),

  /** A split whose row amounts no longer add up to the net; a single range has no amounts, so it stays valid. */
  isSplitStaleForNet: (value: PeriodisationRowsFormValue | undefined, netAmount: number): boolean =>
    !!value?.isSplit &&
    value.rows.some((r) => !!r.amount) &&
    PeriodisationRowsHelper.remaining(value, Math.abs(netAmount)) !== 0,

  isRowComplete,

  isSplitAllInInvoiceMonth,

  /** The invoice month for in-month detection; undefined when the invoice date is missing or invalid. */
  invoiceMonth: (invoiceDate: Date | string | undefined | null): Date | undefined => {
    if (!invoiceDate) return undefined;
    const date = new Date(invoiceDate);
    return DateHelper.isValidDate(date) ? date : undefined;
  },

  /** Amount less VAT, or the whole amount for a company without VAT. Signed: dimensions keep the sign. */
  netAmount: (amount: number, tax: number | undefined, companyNoVat: boolean | undefined): number =>
    companyNoVat ? amount : MathHelper.subtractAmounts(amount, tax ?? 0),

  /** What a value sends: no rows (no periodisation), one row (a plain range) or several rows. */
  rowsToSend,

  toApiRow,

  /**
   * Every periodisation field of the invoice PUT. An absent envelope field is undefined, which JSON.stringify drops;
   * Core reads the missing field as null, so a cleared or month-mode value still overwrites what the invoice had.
   */
  toInvoiceFields: (
    value: PeriodisationRowsFormValue | undefined,
    invoiceMonth: Date | undefined
  ): Pick<
    WintSuperkollAPIModel.IncomingInvoicePut,
    "periodisationStart" | "periodisationEnd" | "periodisationStartDate" | "periodisationEndDate" | "periodisationRows"
  > => {
    const rows = rowsToSend(value, invoiceMonth);
    const fields = envelope(rows, value?.mode ?? "month");
    return {
      periodisationStart: fields.periodisationStart,
      periodisationEnd: fields.periodisationEnd,
      periodisationStartDate: fields.periodisationStartDate,
      periodisationEndDate: fields.periodisationEndDate,
      periodisationRows: rows.length > 1 ? rows.map((r) => toApiRow(r, value!.mode)) : [],
    };
  },
};

export default PeriodisationRowsHelper;
